import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Category } from '../../types';
import { patterns } from '../../data';
import { CategoryCard } from '../../components/CategoryCard/CategoryCard';
import { isNew } from '../../utils/isNew';
import { useReveal } from '../../hooks/useReveal';
import styles from './Home.module.css';

const ALL_CATEGORIES: Category[] = [
  'Transparency',
  'Control',
  'Error Handling',
  'Onboarding',
  'Conversation',
];

const ORIGINAL_DRAFT =
  'Thanks for the thoughtful feedback. Here’s what we’re refining for the next iteration…';
const WARMER_DRAFT =
  'Great discussion, team. I’ve captured your feedback and pulled together our next steps. Let’s keep the momentum going.';
const DEFAULT_FEEDBACK = 'Try the pattern. Review, revise, then approve.';

function HeroDemo() {
  const [approved, setApproved] = useState(false);
  const [revised, setRevised] = useState(false);
  const [feedback, setFeedback] = useState(DEFAULT_FEEDBACK);

  const approve = () => {
    setApproved(true);
    setFeedback('Draft approved in this demo. Nothing has been sent.');
  };

  const reviseOrReset = () => {
    if (approved) {
      setApproved(false);
      setRevised(false);
      setFeedback(DEFAULT_FEEDBACK);
      return;
    }
    const next = !revised;
    setRevised(next);
    setFeedback(
      next
        ? 'Tone revised to feel warmer. Review it before approving.'
        : 'Original draft restored. You decide what happens next.'
    );
  };

  return (
    <div className={styles.stage}>
      <div className={styles.stageGlow} aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className={styles.demo}>
        <div className={styles.demoHead}>
          <div className={styles.demoTitle}>
            <span className={styles.spark} aria-hidden="true">✧</span> Your agent, with boundaries.
          </div>
          <span className={styles.chip}>Control</span>
        </div>
        <div className={styles.message}>Draft a follow-up for the design team.</div>
        <div className={styles.agent}>
          <span className={styles.agentIcon} aria-hidden="true">✧</span>
          <p>I’ve prepared a draft. You have the final say.</p>
        </div>
        <div className={styles.proposal}>
          <div className={styles.proposalTop}>
            <span>REVIEW BEFORE SENDING</span>
            <span>01 / 03</span>
          </div>
          <h3>Design review follow-up</h3>
          <dl>
            <dt>To</dt>
            <dd>Design team</dd>
            <dt>Subject</dt>
            <dd>Next steps from our review</dd>
            <dt>Draft</dt>
            <dd>{revised ? WARMER_DRAFT : ORIGINAL_DRAFT}</dd>
          </dl>
          <div className={styles.demoControls}>
            <button className={styles.approve} onClick={approve} disabled={approved}>
              {approved ? 'Approved ✓' : 'Approve draft'}
            </button>
            <button className={styles.edit} onClick={reviseOrReset}>
              {approved ? 'Reset demo' : 'Revise tone'}
            </button>
          </div>
        </div>
        <p className={styles.feedback} aria-live="polite">{feedback}</p>
      </div>
      <div className={styles.floating}>
        <strong>Designed to keep you in control</strong>
        The agent proposes. The human decides.
      </div>
      <p className={styles.caption}>A little preview of better agent experiences</p>
    </div>
  );
}

export function Home() {
  const location = useLocation();
  useReveal();

  // Header links point at /#patterns and /#approach
  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    el?.scrollIntoView({ behavior: 'smooth' });
  }, [location.hash]);

  const getCategoryCount = (cat: Category) =>
    patterns.filter((p) => p.category === cat).length;

  const getCategoryNewCount = (cat: Category) =>
    patterns.filter((p) => p.category === cat && isNew(p.created_at)).length;

  return (
    <main className={styles.main}>
      {/* Hero */}
      <div className={styles.top}>
        <div className={styles.aurora} aria-hidden="true">
          <span className={styles.blobAqua} />
          <span className={styles.blobLilac} />
          <span className={styles.blobRose} />
          <span className={styles.blobBlue} />
        </div>
        <div className="container">
          <section className={styles.hero}>
            <h1 className={`display ${styles.title}`}>
              <span className={styles.line}>
                <span style={{ animationDelay: '80ms' }}>AX Pattern</span>
              </span>
              <span className={styles.line}>
                <span className={`iridescent ${styles.shimmer}`} style={{ animationDelay: '220ms' }}>
                  Library
                </span>
              </span>
            </h1>
            <div className={styles.heroGrid} data-reveal>
              <div>
                <p className="mono-label">Start here</p>
                <div className={styles.actions}>
                  <span className={styles.glowWrap}>
                    <span className={styles.halo} aria-hidden="true" />
                    <a className={`${styles.btn} ${styles.primary}`} href="#patterns">
                      Explore the patterns
                    </a>
                  </span>
                  <Link className={`${styles.btn} ${styles.secondary}`} to="/quiz">
                    Find my pattern
                  </Link>
                </div>
              </div>
              <div>
                <p className="mono-label">About the library</p>
                <p className={styles.intro}>
                  Design the moments where people and AI work together. A library of patterns
                  for clearer, more considered agent experiences.
                </p>
                <div className={styles.heroNote}>
                  <span>{patterns.length} patterns</span>
                  <span>{ALL_CATEGORIES.length} categories</span>
                  <span>Built for designers</span>
                </div>
              </div>
            </div>
            <HeroDemo />
          </section>
        </div>
      </div>

      <div className={styles.marquee} aria-label="Make intent visible. Keep control human. Design for uncertainty.">
        <div className={styles.marqueeTrack} aria-hidden="true">
          {[0, 1].map((n) => (
            <div className={styles.marqueeGroup} key={n}>
              <span>Make intent visible</span>
              <span className={styles.marqueeStar}>✦</span>
              <span>Keep control human</span>
              <span className={styles.marqueeStar}>✦</span>
              <span>Design for uncertainty</span>
              <span className={styles.marqueeStar}>✦</span>
              <span>Good AI makes room for people</span>
              <span className={styles.marqueeStar}>✦</span>
            </div>
          ))}
        </div>
      </div>

      <div className="container">
        {/* Patterns */}
        <section className={styles.section} id="patterns">
          <div className={styles.sectionHead} data-reveal>
            <div>
              <div className={`mono-label ${styles.small}`}>The pattern collection</div>
              <h2 className={`display ${styles.sectionTitle}`}>
                Better interactions,
                <br />
                one pattern at a time.
              </h2>
            </div>
            <div className={styles.sectionAside}>
              <p>Start with the challenge you’re designing for. Find a practical way forward.</p>
            </div>
          </div>

          <div className={styles.categoryGrid}>
            {ALL_CATEGORIES.map((cat, i) => (
              <CategoryCard
                key={cat}
                category={cat}
                index={i}
                count={getCategoryCount(cat)}
                newCount={getCategoryNewCount(cat)}
                featured={i === ALL_CATEGORIES.length - 1}
              />
            ))}
          </div>
        </section>

        {/* About */}
        <section className={styles.why} id="approach" data-reveal>
          <div>
            <div className={`mono-label ${styles.small}`}>Beyond the chat box</div>
            <h2 className={`display ${styles.sectionTitle}`}>
              AI changes the interface.
              <br />
              <span className={`iridescent ${styles.shimmer}`}>People remain the point.</span>
            </h2>
          </div>
          <p>
            Agents introduce uncertainty, take initiative, and act on our behalf. That calls for
            more than familiar UI components.
            <br />
            <br />
            AX patterns help you design for these moments - with{' '}
            <strong>visible intent, thoughtful boundaries, and room for people to steer.</strong>
          </p>
        </section>

        <section className={`${styles.closing} tint-glow`} data-reveal>
          <h2 className={`display ${styles.sectionTitle}`}>
            Make your next AI experience
            <br />
            a more human one.
          </h2>
          <p>Find the pattern that fits the moment you’re designing.</p>
          <span className={styles.glowWrap}>
            <span className={styles.halo} aria-hidden="true" />
            <Link className={`${styles.btn} ${styles.primary}`} to="/quiz">
              Find your pattern
            </Link>
          </span>
        </section>
      </div>
    </main>
  );
}
